import React, { useEffect, useState } from 'react';
import { get } from '../utils/apiClient';
import { getCachedData, setCachedData, isCacheStale } from '../utils/cacheUtils';
import LoadingSpinner from '../components/LoadingSpinner';
import PageHeader from '../components/PageHeader';
import Base64Image from '../components/Base64Image';
import {
  TestimoniesPageContainer,
  Section,
  SectionTitle,
  IntroText,
  CategoriesBar,
  CategoryButton,
  CategoryDescription,
  PostsGrid,
  PostCard,
  PostImageWrapper,
  PostTitle,
  PostMeta,
  PostDescription,
  FeaturedBadge,
} from '../styles/TestimoniesPageStyles';

const PAGE_CACHE_KEY = 'testimonies_page';
const POSTS_CACHE_BASE_KEY = 'testimonies_posts';
const PAGE_MAX_AGE_MS = 1000 * 60 * 60; // 1 hour
const POSTS_MAX_AGE_MS = 1000 * 60 * 5; // 5 minutes

const TestimoniesPage = () => {
  const [pageData, setPageData] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loadingPage, setLoadingPage] = useState(true);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [error, setError] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('all');

  // Load page config (header, intro, categories) with cache-first strategy
  useEffect(() => {
    let isMounted = true;

    const fetchPage = async () => {
      const cached = await getCachedData(PAGE_CACHE_KEY);

      if (cached && !isCacheStale(cached, PAGE_MAX_AGE_MS)) {
        if (!isMounted) return;
        setPageData(cached.data);
        setLoadingPage(false);
      }

      try {
        const data = await get('/testimonies');
        if (!isMounted) return;
        setPageData(data);
        setCachedData(PAGE_CACHE_KEY, data);
        setLoadingPage(false);
      } catch (err) {
        if (!isMounted) return;
        if (!cached) {
          setLoadingPage(false);
        }
        setError(err && err.message ? err.message : 'Failed to load testimonies page.');
      }
    };

    fetchPage();

    return () => {
      isMounted = false;
    };
  }, []);

  // Load posts list based on category filter (always status=published)
  useEffect(() => {
    let isMounted = true;

    const fetchPosts = async () => {
      const cacheKey = `${POSTS_CACHE_BASE_KEY}_${categoryFilter}`;
      const cached = await getCachedData(cacheKey);

      if (cached && !isCacheStale(cached, POSTS_MAX_AGE_MS)) {
        if (!isMounted) return;
        setPosts(Array.isArray(cached.data) ? cached.data : []);
        setLoadingPosts(false);
      } else {
        if (!isMounted) return;
        setLoadingPosts(true);
      }

      try {
        const params = new URLSearchParams();
        params.set('status', 'published');
        if (categoryFilter !== 'all') {
          params.set('category', categoryFilter);
        }

        const path = `/testimonies/posts?${params.toString()}`;
        const data = await get(path);
        if (!isMounted) return;
        const list = Array.isArray(data) ? data : [];
        setPosts(list);
        setCachedData(cacheKey, list);
        setLoadingPosts(false);
      } catch (err) {
        if (!isMounted) return;
        setLoadingPosts(false);
        setError(err && err.message ? err.message : 'Failed to load testimonies.');
      }
    };

    fetchPosts();

    return () => {
      isMounted = false;
    };
  }, [categoryFilter]);

  const header = pageData && pageData.header ? pageData.header : null;
  const introText = pageData ? pageData.introText : null;
  const categories = pageData && Array.isArray(pageData.categories) ? pageData.categories : [];

  const getCategoryName = (key) => {
    if (!key || !categories.length) return null;
    const found = categories.find((category) => category.key === key);
    return found && found.name ? found.name : null;
  };

  const activeCategory =
    categoryFilter !== 'all'
      ? categories.find((category) => category.key === categoryFilter)
      : null;

  if (loadingPage && !pageData) {
    return (
      <TestimoniesPageContainer>
        <LoadingSpinner />
      </TestimoniesPageContainer>
    );
  }

  if (error && !pageData && !posts.length) {
    return <TestimoniesPageContainer>{error}</TestimoniesPageContainer>;
  }

  return (
    <TestimoniesPageContainer>
      {header && (
        <PageHeader
          title={header.title}
          image={header.backgroundImage}
        />
      )}

      {(introText || categories.length > 0) && (
        <Section>
          {introText && (
            <>
              <SectionTitle>Introduction</SectionTitle>
              <IntroText>{introText}</IntroText>
            </>
          )}

          {categories.length > 0 && (
            <>
              <SectionTitle>Categories</SectionTitle>
              <CategoriesBar>
                <CategoryButton
                  type="button"
                  $active={categoryFilter === 'all'}
                  onClick={() => setCategoryFilter('all')}
                >
                  All
                </CategoryButton>
                {categories.map((category) => (
                  <CategoryButton
                    key={category.key || category.name}
                    type="button"
                    $active={categoryFilter === category.key}
                    onClick={() => setCategoryFilter(category.key || 'other')}
                  >
                    {category.name || category.key}
                  </CategoryButton>
                ))}
              </CategoriesBar>

              {activeCategory && activeCategory.description && (
                <CategoryDescription>
                  {activeCategory.description}
                </CategoryDescription>
              )}
            </>
          )}
        </Section>
      )}

      <Section>
        <SectionTitle>Testimonies</SectionTitle>

        {loadingPosts && <LoadingSpinner />}

        {!loadingPosts && posts.length === 0 && (
          <IntroText>
            No testimonies found
            {categoryFilter !== 'all' ? ' in this category yet.' : ' yet.'}
          </IntroText>
        )}

        {!loadingPosts && posts.length > 0 && (
          <PostsGrid>
            {posts.map((post, index) => {
              const dateLabel = post.date
                ? new Date(post.date).toLocaleDateString()
                : null;
              const categoryLabel =
                getCategoryName(post.category) || post.category;

              return (
                <PostCard
                  key={post._id || post.id || `${post.title || 'post'}-${index}`}
                >
                  {post.image && (
                    <PostImageWrapper>
                      <Base64Image
                        image={post.image}
                        alt={post.title || 'Testimony'}
                        style={{ width: '100%', borderRadius: 6 }}
                      />
                    </PostImageWrapper>
                  )}

                  {post.title && (
                    <PostTitle>
                      {post.title}
                      {post.isFeatured && (
                        <FeaturedBadge>Featured</FeaturedBadge>
                      )}
                    </PostTitle>
                  )}

                  {(post.author || dateLabel || categoryLabel) && (
                    <PostMeta>
                      {post.author && <span>{post.author}</span>}
                      {post.author && (dateLabel || categoryLabel) && (
                        <span> • </span>
                      )}
                      {dateLabel && <span>{dateLabel}</span>}
                      {(post.author || dateLabel) && categoryLabel && (
                        <span> • </span>
                      )}
                      {categoryLabel && <span>{categoryLabel}</span>}
                    </PostMeta>
                  )}

                  {post.description && (
                    <PostDescription>{post.description}</PostDescription>
                  )}
                </PostCard>
              );
            })}
          </PostsGrid>
        )}
      </Section>
    </TestimoniesPageContainer>
  );
};

export default TestimoniesPage;

